import { Badge, Button, EmptyState, getTabbarIds, Notice, Spinner, Tabbar, type TabbarTab } from '@bennows/boows-react';
import { Activity, ArrowLeft, Ban, Building2, CreditCard, Receipt, History, LayoutDashboard, LockOpen, Shapes, Sparkles, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { ApiFailure } from '../../lib/api';
import { formatDate, formatDateTime } from '../../lib/format';
import type { OperatorMe } from '../auth/useOperatorMe';
import { BillingChangeDialog } from './BillingChangeDialog';
import { TenantActionDialog, type TenantAction } from './TenantActionDialog';
import { TenantAiTab } from './TenantAiTab';
import { TenantAuditTab } from './TenantAuditTab';
import { TenantBillingTab } from './TenantBillingTab';
import { TenantOverviewTab } from './TenantOverviewTab';
import { TenantProblemsTab } from './TenantProblemsTab';
import { TenantUsageTab } from './TenantUsageTab';
import { useTenant } from './useTenant';

const TABS = [
  { id: 'overview', icon: <LayoutDashboard aria-hidden="true" size={16} /> },
  { id: 'usage', icon: <Activity aria-hidden="true" size={16} /> },
  { id: 'ai', icon: <Sparkles aria-hidden="true" size={16} /> },
  { id: 'billing', icon: <CreditCard aria-hidden="true" size={16} /> },
  { id: 'problems', icon: <TriangleAlert aria-hidden="true" size={16} /> },
  { id: 'audit', icon: <History aria-hidden="true" size={16} /> },
] as const;
type TabId = (typeof TABS)[number]['id'];
const TAB_PREFIX = 'tenant';
/** Tabs only some roles see; the API refuses them to everyone else as well. */
const TAB_PERMISSIONS: Partial<Record<TabId, OperatorMe['permissions'][number]>> = { billing: 'billing:read' };

/** One tenant: master data and live metrics, never content (REQ-140-012); every load is audited (REQ-140-005). */
export function TenantPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useOutletContext<OperatorMe>();
  const [dialog, setDialog] = useState<TenantAction | 'billing' | null>(null);
  const { id = '', tab } = useParams();
  const visible = TABS.filter((entry) => !TAB_PERMISSIONS[entry.id] || me.permissions.includes(TAB_PERMISSIONS[entry.id]!));
  const active: TabId = visible.some((entry) => entry.id === tab) ? (tab as TabId) : 'overview';
  const tenant = useTenant(id);
  const loaded = tenant.data !== undefined;
  // The tab row scrolls on a phone: keep the active tab (e.g. a deep link to /billing) in view.
  useEffect(() => {
    if (loaded) document.getElementById(getTabbarIds(TAB_PREFIX, active).tabId)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [active, loaded]);
  const back = (
    <Link className="ui-console-back" to="/tenants">
      <ArrowLeft aria-hidden="true" size={16} />
      {t('tenant.back')}
    </Link>
  );

  if (tenant.isPending) {
    return (
      <div className="ui-stack">
        {back}
        <div className="ui-console-loading" role="status">
          <Spinner label={t('common.loading')} />
        </div>
      </div>
    );
  }
  if (tenant.isError) {
    const missing = tenant.error instanceof ApiFailure && tenant.error.status === 404;
    return (
      <div className="ui-stack">
        {back}
        {missing ? (
          <EmptyState icon={<Building2 aria-hidden="true" />} title={t('tenant.notFoundTitle')} hint={t('tenant.notFoundText')} />
        ) : (
          <div className="ui-error-state" role="alert">
            <p className="ui-error-state__text">{t('tenant.loadError')}</p>
            <Button variant="secondary" onClick={() => void tenant.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  const detail = tenant.data;
  const tabs: TabbarTab[] = visible.map((entry) => ({ id: entry.id, label: t(`tenant.tabs.${entry.id}`), icon: entry.icon, ...(entry.id === 'problems' && detail.problems.length > 0 ? { count: detail.problems.length } : {}) }));
  const { tabId, panelId } = getTabbarIds(TAB_PREFIX, active);
  const suspension = detail.suspension;
  const billingMode = detail.metrics.billing.state;
  const actions = (
    <>
      {me.permissions.includes('tenants:manage') ? (
        <Button variant="secondary" iconPrefix={<Shapes aria-hidden="true" />} onClick={() => setDialog('industry')}>
          {t('tenantActions.open.industry')}
        </Button>
      ) : null}
      {me.permissions.includes('tenants:manage') && (billingMode === 'metered' || billingMode === 'exempt') ? (
        <Button variant="secondary" iconPrefix={<Receipt aria-hidden="true" />} onClick={() => setDialog('billing')}>
          {t('tenantBilling.change.open')}
        </Button>
      ) : null}
      {me.permissions.includes('tenants:suspend') ? (
        suspension ? (
          <Button variant="secondary" iconPrefix={<LockOpen aria-hidden="true" />} onClick={() => setDialog('unsuspend')}>
            {t('tenantActions.open.unsuspend')}
          </Button>
        ) : (
          <Button variant="danger" iconPrefix={<Ban aria-hidden="true" />} onClick={() => setDialog('suspend')}>
            {t('tenantActions.open.suspend')}
          </Button>
        )
      ) : null}
    </>
  );
  return (
    <div className="ui-stack">
      {back}
      <PageHeader
        title={
          suspension ? (
            <span className="ui-console-name">
              {detail.name}
              <Badge tone="danger">{t('tenant.suspendedBadge')}</Badge>
            </span>
          ) : (
            detail.name
          )
        }
        description={t('tenant.subtitle', { slug: detail.slug, industry: t(`industryProfiles.${detail.industry_profile}`), date: formatDate(detail.created_at) })}
        actions={actions}
      />
      {suspension ? (
        <Notice tone="danger" role="none" title={t('tenant.suspendedNotice.title', { date: formatDateTime(suspension.suspended_at) })}>
          {t('tenant.suspendedNotice.reason', { reason: suspension.reason })}
        </Notice>
      ) : null}
      <p className="ui-console-muted">{t('tenant.auditedNote')}</p>
      <Tabbar
        variant="pills"
        className="ui-tabbar-host"
        ariaLabel={t('tenant.tabsLabel')}
        idPrefix={TAB_PREFIX}
        tabs={tabs}
        active={active}
        onChange={(next) => navigate(next === 'overview' ? `/tenants/${id}` : `/tenants/${id}/${next}`, { replace: true })}
      />
      <div role="tabpanel" id={panelId} aria-labelledby={tabId} className="ui-stack">
        {active === 'overview' ? <TenantOverviewTab tenant={detail} /> : null}
        {active === 'usage' ? <TenantUsageTab tenantId={detail.id} metrics={detail.metrics} /> : null}
        {active === 'ai' ? <TenantAiTab tenantId={detail.id} /> : null}
        {active === 'billing' ? <TenantBillingTab tenantId={detail.id} tenantName={detail.name} permissions={me.permissions} /> : null}
        {active === 'problems' ? <TenantProblemsTab problems={detail.problems} /> : null}
        {active === 'audit' ? <TenantAuditTab tenantId={detail.id} /> : null}
      </div>
      {dialog === 'billing' && (billingMode === 'metered' || billingMode === 'exempt') ? (
        <BillingChangeDialog tenantId={detail.id} tenantName={detail.name} mode={billingMode} onClose={() => setDialog(null)} />
      ) : dialog && dialog !== 'billing' ? (
        <TenantActionDialog tenant={detail} action={dialog} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
